import { LinearFilter, SRGBColorSpace, VideoTexture } from 'three';

/**
 * One decoded video stream and the texture that samples it.
 *
 * Every shard owns an instance. That is deliberate and it is the expensive part
 * of Mirror Drift: a single `HTMLVideoElement` has exactly one `currentTime`, so
 * showing the same MV at six different moments genuinely requires six decoders.
 * The alternative — one decoder writing frames into a history atlas — would only
 * ever offer recent frames rather than arbitrary offsets, so the honest
 * implementation is measured instead of assumed.
 *
 * Audio is never enabled. The MV is scenery inside the world; the song is the
 * runtime's business and the two must not overlap.
 */

export interface MirrorVideoSettings {
  /** Multiplier on real time. Values either side of 1 desynchronise the shards. */
  readonly playbackRate: number;
  /** Seconds into the video to start from. Wrapped by the real duration on load. */
  readonly offsetSeconds: number;
}

export type MirrorVideoState = 'idle' | 'loading' | 'ready' | 'failed';

export class MirrorVideoSource {
  readonly element: HTMLVideoElement;
  private videoTexture: VideoTexture | null = null;
  private status: MirrorVideoState = 'idle';
  private requestedOffset = 0;
  private lastUploadedTime = -1;
  private disposed = false;

  constructor(private readonly settings: MirrorVideoSettings) {
    const element = document.createElement('video');
    // `muted` must be set before any play() attempt or autoplay policy rejects it.
    element.muted = true;
    element.defaultMuted = true;
    element.volume = 0;
    element.playsInline = true;
    element.loop = true;
    element.preload = 'auto';
    element.crossOrigin = 'anonymous';
    element.disableRemotePlayback = true;
    // Never in the document: the element exists only to feed a texture.
    element.style.display = 'none';
    this.element = element;
    this.requestedOffset = settings.offsetSeconds;
  }

  get state(): MirrorVideoState {
    return this.status;
  }

  get texture(): VideoTexture | null {
    return this.videoTexture;
  }

  /** Intrinsic video aspect, or null before metadata arrives. */
  get aspect(): number | null {
    const { videoWidth, videoHeight } = this.element;
    return videoWidth > 0 && videoHeight > 0 ? videoWidth / videoHeight : null;
  }

  get duration(): number {
    const value = this.element.duration;
    return Number.isFinite(value) && value > 0 ? value : 0;
  }

  /**
   * Loads `url` and waits until a frame can actually be presented.
   *
   * Resolves false rather than throwing: a missing MV must disable Mirror Drift,
   * never take the world down with it.
   */
  async load(url: string, timeoutMs = 12000): Promise<boolean> {
    if (this.disposed) return false;
    this.status = 'loading';

    const element = this.element;
    const ready = new Promise<boolean>((resolve) => {
      let settled = false;
      const finish = (value: boolean) => {
        if (settled) return;
        settled = true;
        cleanup();
        resolve(value);
      };
      const onReady = () => finish(true);
      const onError = () => finish(false);
      const timer = window.setTimeout(() => finish(false), timeoutMs);
      const cleanup = () => {
        window.clearTimeout(timer);
        element.removeEventListener('loadeddata', onReady);
        element.removeEventListener('error', onError);
        element.removeEventListener('stalled', onError);
      };
      // `loadeddata` rather than `loadedmetadata`: metadata alone gives duration
      // but not a decoded frame, and seeking before that produces a black texture.
      element.addEventListener('loadeddata', onReady, { once: true });
      element.addEventListener('error', onError, { once: true });
    });

    element.src = url;
    element.load();

    const loaded = await ready;
    if (this.disposed) return false;
    if (!loaded) {
      this.status = 'failed';
      return false;
    }

    this.applyOffset();
    element.playbackRate = this.settings.playbackRate;

    this.videoTexture = new VideoTexture(element);
    this.videoTexture.colorSpace = SRGBColorSpace;
    this.videoTexture.minFilter = LinearFilter;
    this.videoTexture.magFilter = LinearFilter;
    // A video texture is re-uploaded every frame; mipmaps would be rebuilt each
    // time for no benefit at these on-screen sizes.
    this.videoTexture.generateMipmaps = false;

    this.status = 'ready';
    return true;
  }

  /**
   * Starts playback. Resolves false when the autoplay policy blocks it, so the
   * caller can retry from a user gesture.
   */
  async play(): Promise<boolean> {
    if (this.status !== 'ready') return false;
    try {
      await this.element.play();
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Uploads the current video frame if it has advanced since the last upload.
   *
   * `VideoTexture` normally schedules its own uploads from
   * `requestVideoFrameCallback`, which fires only while the page is presenting
   * frames. That is a real dependency, not a theoretical one: driving the render
   * loop manually leaves the callback dormant and every shard samples an
   * never-uploaded — that is, black — texture.
   *
   * Gating on `currentTime` keeps this cheap. The MVs run at 30 fps against a 60
   * fps render, so blindly flagging the texture every frame would double the
   * upload traffic for frames that have not changed.
   */
  refresh(): void {
    const texture = this.videoTexture;
    if (texture === null) return;
    const element = this.element;
    if (element.readyState < element.HAVE_CURRENT_DATA) return;
    if (element.currentTime === this.lastUploadedTime) return;
    this.lastUploadedTime = element.currentTime;
    texture.needsUpdate = true;
  }

  pause(): void {
    if (!this.element.paused) this.element.pause();
  }

  get playing(): boolean {
    return !this.element.paused && !this.element.ended;
  }

  /** Seeks to the configured offset, wrapped into the real duration. */
  private applyOffset(): void {
    const duration = this.duration;
    if (duration <= 0) return;
    const wrapped = ((this.requestedOffset % duration) + duration) % duration;
    try {
      this.element.currentTime = wrapped;
    } catch {
      // Seeking can throw while the element is still settling; the offset is a
      // presentation detail, so a failure here is not worth surfacing.
    }
  }

  /** Frames decoded and dropped so far, when the browser exposes them. */
  quality(): { total: number; dropped: number } | null {
    const element = this.element as HTMLVideoElement & {
      getVideoPlaybackQuality?: () => { totalVideoFrames: number; droppedVideoFrames: number };
    };
    if (typeof element.getVideoPlaybackQuality !== 'function') return null;
    const quality = element.getVideoPlaybackQuality();
    return { total: quality.totalVideoFrames, dropped: quality.droppedVideoFrames };
  }

  /**
   * Releases the decoder and the texture, leaving the source reusable.
   *
   * The difference from `dispose` matters now that a session plays more than one
   * song: switching songs has to let go of the previous MV — its GPU texture,
   * its decoder, and any object URL the caller made for it — without destroying
   * the shard, which is going to show the *next* song's video in a moment.
   *
   * Clearing `src` and reloading is what actually releases the decoder. Dropping
   * the reference alone leaves it alive until garbage collection gets around to
   * it, which for six 200 MB videos is not soon enough.
   */
  release(): void {
    this.videoTexture?.dispose();
    this.videoTexture = null;
    this.element.pause();
    this.element.removeAttribute('src');
    this.element.load();
    this.lastUploadedTime = -1;
    this.status = 'idle';
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.release();
  }
}
